// Nodes view: spot-vs-on-demand cost + coverage header cards, and a sortable
// per-node table (cost, price, capacity, allocation, live utilization). Reads
// /finops/v2/.../nodes. Blank-node pod history means util may be absent; the
// node cost view is unaffected (node_metrics is complete).

import React, { useState, useMemo } from 'react';
import { Card, CardContent } from '../ui/card';
import { Download, ArrowUpDown, Zap } from 'lucide-react';
import { useNodes } from '../../hooks/useFinopsV2';
import { formatMoney, formatPct, formatCores, formatGB, downloadCsv, SEMANTIC } from './finopsFormat';
import { StatStrip, StatCell, SectionHeader, UtilBar, Skeleton, ChDisabledCard, EmptyCard } from './finopsUi';

export default function NodesPanel({ clusterId, hours }) {
  const { data, loading, available } = useNodes(clusterId, hours);
  const [colSort, setColSort] = useState({ key: 'cost_usd', dir: 'desc' });

  const rows = useMemo(() => {
    const items = (data?.nodes || []).slice();
    items.sort((a, b) => {
      const av = a[colSort.key], bv = b[colSort.key];
      if (typeof av === 'string') return colSort.dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      return colSort.dir === 'asc' ? (av ?? 0) - (bv ?? 0) : (bv ?? 0) - (av ?? 0);
    });
    return items;
  }, [data, colSort]);

  const onHeader = (key) => setColSort((s) => ({ key, dir: s.key === key && s.dir === 'desc' ? 'asc' : 'desc' }));

  if (!available) return <ChDisabledCard title="Nodes view unavailable" />;
  if (loading) return (
    <div className="space-y-4">
      <Skeleton className="h-24" />
      <Skeleton className="h-72" />
    </div>
  );
  if (!data || !data.node_count) return <EmptyCard message="No node data in this window" />;

  const spotPct = data.spot_coverage_pct ?? 0;

  return (
    <div className="space-y-6">
      <StatStrip>
        <StatCell label="Total node cost" value={formatMoney(data.total_cost_usd, { compact: true })}
                  sub={`${data.node_count} nodes · ${data.window_hours}h`} />
        <StatCell label="Spot cost" value={formatMoney(data.spot_cost_usd, { compact: true })}
                  sub={`${data.spot_node_count} spot nodes`}
                  tone="text-violet-600 dark:text-violet-400" />
        <StatCell label="On-demand cost" value={formatMoney(data.on_demand_cost_usd, { compact: true })}
                  sub={`${data.on_demand_node_count} on-demand nodes`} />
        <StatCell label="Spot coverage" value={formatPct(spotPct)}
                  sub="share of spend on spot"
                  tone={spotPct >= 50 ? 'text-emerald-600 dark:text-emerald-400' : undefined} />
      </StatStrip>

      {/* spot vs on-demand split bar */}
      <Card className="border border-slate-200 dark:border-surface-border bg-white dark:bg-surface-dark shadow-sm">
        <CardContent className="p-5">
          <SectionHeader title="Capacity mix" hint="Cost split — spot is the savings lever" />
          <div className="flex h-3 rounded-full overflow-hidden bg-slate-100 dark:bg-slate-700">
            <div style={{ width: `${spotPct}%`, background: SEMANTIC.spot }} title={`Spot ${formatPct(spotPct)}`} />
            <div style={{ width: `${100 - spotPct}%`, background: SEMANTIC.on_demand }} title={`On-demand ${formatPct(100 - spotPct)}`} />
          </div>
          <div className="flex items-center gap-4 mt-2 text-xs text-slate-500 dark:text-text-secondary">
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: SEMANTIC.spot }} />Spot {formatPct(spotPct)}</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: SEMANTIC.on_demand }} />On-demand {formatPct(100 - spotPct)}</span>
          </div>
        </CardContent>
      </Card>

      <Card className="border border-slate-200 dark:border-surface-border bg-white dark:bg-surface-dark shadow-sm">
        <CardContent className="p-5">
          <SectionHeader title="Nodes" hint={`${data.node_count} nodes · last ${data.window_hours}h`}>
            <button onClick={() => downloadCsv(`nodes-${clusterId}.csv`, [
              { key: 'node', label: 'Node' }, { key: 'instance_type', label: 'Instance Type' },
              { key: 'capacity_type', label: 'Capacity Type' }, { key: 'cost_usd', label: 'Cost USD' },
              { key: 'hourly_usd', label: 'Hourly USD' }, { key: 'node_hours', label: 'Node Hours' },
              { key: 'cpu_capacity_cores', label: 'CPU Cap' }, { key: 'cpu_alloc_pct', label: 'CPU Alloc %' },
              { key: 'cpu_util_pct', label: 'CPU Util %' },
            ], rows)}
              className="inline-flex items-center gap-1 text-xs font-medium text-slate-600 dark:text-text-secondary hover:text-slate-900 dark:hover:text-white border border-slate-200 dark:border-surface-border rounded-md px-2 py-1">
              <Download className="w-3.5 h-3.5" /> CSV
            </button>
          </SectionHeader>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 dark:border-surface-border">
                  {[
                    { key: 'node', label: 'Node', align: 'left' },
                    { key: 'capacity_type', label: 'Type', align: 'left' },
                    { key: 'cost_usd', label: 'Cost', align: 'right' },
                    { key: 'hourly_usd', label: '$/hr', align: 'right' },
                    { key: 'cpu_capacity_cores', label: 'CPU', align: 'right' },
                    { key: 'cpu_alloc_pct', label: 'Allocated', align: 'right' },
                    { key: 'cpu_util_pct', label: 'Utilized', align: 'right' },
                  ].map((c) => (
                    <th key={c.key} className={`py-2 px-2 text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-text-secondary ${c.align === 'right' ? 'text-right' : 'text-left'}`}>
                      <button onClick={() => onHeader(c.key)} className={`inline-flex items-center gap-1 hover:text-slate-700 dark:hover:text-white ${c.align === 'right' ? 'flex-row-reverse' : ''}`}>
                        {c.label}<ArrowUpDown className="w-3 h-3 opacity-40" />
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((n) => (
                  <tr key={n.node} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/40">
                    <td className="py-2 px-2">
                      <div className="font-medium text-slate-900 dark:text-white truncate max-w-[240px]">{n.node}</div>
                      <div className="text-xs text-slate-400 dark:text-slate-500">{n.instance_type || '—'}</div>
                    </td>
                    <td className="py-2 px-2">
                      <span className={`inline-flex items-center gap-1 text-xs font-medium px-1.5 py-0.5 rounded ${
                        n.is_spot ? 'bg-violet-50 dark:bg-violet-500/10 text-violet-600 dark:text-violet-400'
                                  : 'bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400'}`}>
                        {n.is_spot && <Zap className="w-3 h-3" />}{n.capacity_type}
                      </span>
                    </td>
                    <td className="py-2 px-2 text-right tabular-nums text-slate-900 dark:text-white">{formatMoney(n.cost_usd)}</td>
                    <td className="py-2 px-2 text-right tabular-nums text-slate-600 dark:text-text-secondary">${Number(n.hourly_usd || 0).toFixed(4)}</td>
                    <td className="py-2 px-2 text-right tabular-nums text-slate-600 dark:text-text-secondary">{formatCores(n.cpu_capacity_cores)}</td>
                    <td className="py-2 px-2"><div className="flex justify-end"><UtilBar pct={n.cpu_alloc_pct} tint="bg-blue-500" /></div></td>
                    <td className="py-2 px-2"><div className="flex justify-end"><UtilBar pct={n.cpu_util_pct} tint="bg-emerald-500" /></div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
